import { CButton, CContainer } from "@coreui/react"
import DeloitteLogo from "assets/icons/brand/Deloitte.svg"
import { ReactComponent as HamburgerIcon } from "assets/icons/hamburgerIcon.svg"
import classNames from "classnames"
import FeedbackDrawerProvider from "components/common/Context/FeedbackDrawerContext"
import MediaProvider from "components/common/Context/MediaContext"
import { GreyedOut } from "components/common/display"
import { FeedbackDrawer } from "components/common/layout"
import { LoadingMessage } from "components/common/loading"
import PropTypes from "prop-types"
import React, { Suspense, useContext, useEffect, useRef, useState } from "react"
import ReactDOM from "react-dom"
import { useSelector } from "react-redux"
import { SWRConfig } from "swr"

import ProjectAccountsMonitor from "./ProjectAccountsMonitor"
const DefaultHeader = React.lazy(() => import("./DefaultHeader"))
const DefaultAside = React.lazy(() => import("./DefaultAside"))

export function DefaultLayout({ children }) {
  const [sidebarIsOpen, setSidebarIsOpen] = useState(false)
  const [mblSidebar, setMblSidebar] = useState(false)
  const isAuthenticated = useSelector((state) => state.user.isAuthenticated)
  const { isSmallScreen, setIsSmallScreen } = useContext(MediaProvider)
  const sidebarRef = useRef(null)

  useEffect(() => {
    if (isAuthenticated) {
      const isCMSOpenCloudPortal =
        window.location.hostname === "cms.opencloudportal.com"
      const cookieScript = document.createElement("script")
      cookieScript.src =
        "https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
      cookieScript.type = "text/javascript"
      cookieScript.setAttribute(
        "data-domain-script",
        `0bdad06a-93a8-4f44-ad52-7f9a9aaaae32${
          isCMSOpenCloudPortal ? "" : "-test"
        }`
      )
      document.head.appendChild(cookieScript)
    }
  }, [isAuthenticated])

  useEffect(() => {
    const handleMediaChange = (media) => {
      if (media.matches) {
        setIsSmallScreen(true)
      } else setIsSmallScreen(false)
    }
    const mediaQuery = window.matchMedia("(max-width: 600px)")
    mediaQuery.addListener(handleMediaChange)
    handleMediaChange(mediaQuery)
    return () => {
      mediaQuery.removeListener(handleMediaChange)
    }
  }, [setIsSmallScreen])

  useEffect(() => {
    function handleClickOutside(event) {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setMblSidebar(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [sidebarRef, setMblSidebar])

  const { isFeedbackDrawerOpen, setIsFeedbackDrawerOpen } = useContext(
    FeedbackDrawerProvider
  )

  const sidebar = isAuthenticated ? (
    <Suspense fallback={<LoadingMessage />}>
      <DefaultAside isOpen={sidebarIsOpen} setIsOpen={setSidebarIsOpen} />
    </Suspense>
  ) : (
    ""
  )

  const header = isAuthenticated ? (
    <Suspense fallback={<LoadingMessage />}>
      <DefaultHeader
        sidebarIsOpen={sidebarIsOpen}
        setSidebarIsOpen={setSidebarIsOpen}
        className={classNames("app-header", {
          "app-header--sidebar-closed": !sidebarIsOpen,
          "app-header--sidebar-open": sidebarIsOpen
        })}
      />
      {sidebar}
    </Suspense>
  ) : (
    ""
  )

  const mobileHeader = isAuthenticated ? (
    <>
      <Suspense fallback={<LoadingMessage />}>
        <div ref={sidebarRef}>
          <div className="w-100 header-color mbl-pos d-flex flex-row justify-content-center align-items-center">
            <CButton
              onClick={() => setMblSidebar(!mblSidebar)}
              className="header-color d-flex justify-content-start position-fixed left-0"
            >
              <HamburgerIcon size="20px" />
            </CButton>
            <div className="ml-2">
              <img
                src={DeloitteLogo}
                alt={"deloitte-logo"}
                height="30px"
                width="60px"
              />
            </div>
          </div>
          <DefaultAside
            mobileScreen={isSmallScreen}
            isOpen={mblSidebar}
            setIsOpen={setMblSidebar}
          />
        </div>
      </Suspense>
    </>
  ) : (
    ""
  )

  const feedbackDrawer = isAuthenticated ? (
    <Suspense fallback={<LoadingMessage />}>
      {ReactDOM.createPortal(
        <FeedbackDrawer
          isOpen={isFeedbackDrawerOpen}
          setSidebarIsOpen={setIsFeedbackDrawerOpen}
        />,
        document.getElementById("drawerHolder")
      )}
    </Suspense>
  ) : (
    ""
  )

  const projectAccountsMonitor = isAuthenticated ? (
    <ProjectAccountsMonitor />
  ) : (
    ""
  )

  return (
    <SWRConfig
      value={{
        revalidateIfStale: false,
        revalidateOnReconnect: false,
        refreshWhenOffline: false,
        revalidateOnFocus: false
      }}
    >
      <CContainer className="app m-0 p-0" fluid id="app-container">
        {isSmallScreen ? mobileHeader : header}
        {feedbackDrawer}
        {projectAccountsMonitor}
        {isSmallScreen ? (
          <GreyedOut enable={mblSidebar} mblScreen={true}>
            <CContainer
              className={classNames("app-body", {
                "app-body--unauthenticated": !isAuthenticated,
                "app-body--sidebar-closed": !sidebarIsOpen && isAuthenticated,
                "app-body--sidebar-open": sidebarIsOpen && isAuthenticated
              })}
              tag="main"
              fluid
            >
              {children}
            </CContainer>
          </GreyedOut>
        ) : (
          <CContainer
            className={classNames("app-body", {
              "app-body--unauthenticated": !isAuthenticated,
              "app-body--sidebar-closed": !sidebarIsOpen && isAuthenticated,
              "app-body--sidebar-open": sidebarIsOpen && isAuthenticated
            })}
            tag="main"
            fluid
          >
            {children}
          </CContainer>
        )}
      </CContainer>
    </SWRConfig>
  )
}

DefaultLayout.propTypes = {
  children: PropTypes.node
}

export default DefaultLayout
